import React, { useState, useEffect, useMemo } from "react";
import { Modal, Input, Tag, Spin, Toast, Button } from "@douyinfe/semi-ui";
import { IconSearch } from "@douyinfe/semi-icons";
import { ApiClient } from "../api/ApiClient";

const apiClient = ApiClient.getDefault();

interface User {
  id: number;
  name: string;
  username: string;
  mobile: string;
  roleName: string;
  teamName: string;
  city?: string;
}

interface UserSwitchModalProps {
  visible: boolean;
  onClose: () => void;
  onSwitch: (username: string, password: string) => Promise<void>;
}

function getRoleColor(r: string): string {
  const m: Record<string, string> = {
    系统管理员: "red", 运营主管: "orange", 销售主管: "blue",
    销售: "green", 按揭专员: "purple", 学员: "cyan", 区长: "violet", 监控员: "grey",
  };
  return m[r] || "grey";
}

function getAvatarColor(name: string): string {
  const colors = ["#6366f1","#8b5cf6","#a855f7","#d946ef","#ec4899","#f43f5e","#f97316","#eab308","#22c55e","#14b8a6","#06b6d4","#3b82f6"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

export default function UserSwitchModal({ visible, onClose, onSwitch }: UserSwitchModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [password, setPassword] = useState("");
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (visible) {
      loadUsers();
      setSelectedUser(null);
      setPassword("");
      setSearchText("");
    }
  }, [visible]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request<User[]>("/users/allUsers", "GET");
      setUsers(resp.data || []);
    } catch (e) {
      Toast.error({ content: "加载用户列表失败" });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchText) return users;
    const t = searchText.toLowerCase();
    return users.filter((u) =>
      u.name?.toLowerCase().includes(t) ||
      u.username?.toLowerCase().includes(t) ||
      u.mobile?.includes(t) ||
      u.teamName?.toLowerCase().includes(t) ||
      u.roleName?.toLowerCase().includes(t)
    );
  }, [users, searchText]);

  const handleSwitch = async () => {
    if (!selectedUser) { Toast.warning({ content: "请选择用户" }); return; }
    if (!password) { Toast.warning({ content: "请输入密码" }); return; }
    setSwitching(true);
    try {
      await onSwitch(selectedUser.mobile || selectedUser.username, password);
    } finally {
      setSwitching(false);
    }
  };

  const hl = (text: string) => {
    if (!searchText || !text) return text;
    const i = text.toLowerCase().indexOf(searchText.toLowerCase());
    if (i === -1) return text;
    return <>{text.slice(0, i)}<span style={{ color: "var(--semi-color-primary)", fontWeight: 600 }}>{text.slice(i, i + searchText.length)}</span>{text.slice(i + searchText.length)}</>;
  };

  return (
    <Modal title="切换用户" visible={visible} onCancel={onClose} footer={null} width={560} style={{ top: 40 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <Input prefix={<IconSearch />} placeholder="搜索姓名、用户名、手机、团队、角色..." value={searchText} onChange={setSearchText} showClear />

        <div style={{ fontSize: 12, color: "var(--semi-color-text-2)" }}>
          共 {users.length} 人，{filteredUsers.length} 个匹配
        </div>

        {/* 用户表格 */}
        <div style={{ height: 380, overflow: "auto", border: "1px solid var(--semi-color-border)", borderRadius: 6 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>
          ) : filteredUsers.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "var(--semi-color-text-3)" }}>未找到匹配的用户</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--semi-color-fill-1)", position: "sticky", top: 0, zIndex: 1 }}>
                  <th style={thStyle}></th>
                  <th style={thStyle}>姓名</th>
                  <th style={thStyle}>团队</th>
                  <th style={thStyle}>角色</th>
                  <th style={thStyle}>手机</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const isSelected = selectedUser?.id === u.id;
                  return (
                    <tr
                      key={u.id}
                      onClick={() => { setSelectedUser(isSelected ? null : u); setPassword(""); }}
                      style={{
                        cursor: "pointer",
                        background: isSelected ? "var(--semi-color-primary-light-default)" : "transparent",
                        borderBottom: "1px solid var(--semi-color-border)",
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "var(--semi-color-fill-0)"; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={{ ...tdStyle, width: 36, textAlign: "center" }}>
                        {isSelected ? (
                          <div style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--semi-color-primary)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto" }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M5 12L10 17L19 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                        ) : (
                          <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid var(--semi-color-border)", margin: "0 auto" }} />
                        )}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 26, height: 26, borderRadius: 6, background: getAvatarColor(u.name), display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                            {u.name?.charAt(0)}
                          </div>
                          <span style={{ fontWeight: 600 }}>{hl(u.name)}</span>
                        </div>
                      </td>
                      <td style={tdStyle}>{hl(u.teamName)}</td>
                      <td style={tdStyle}>
                        <Tag size="small" color={getRoleColor(u.roleName) as any} style={{ margin: 0 }}>{u.roleName}</Tag>
                      </td>
                      <td style={{ ...tdStyle, color: "var(--semi-color-text-2)" }}>{u.mobile || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 选中用户 + 密码 */}
        {selectedUser && (
          <div style={{ padding: 12, background: "var(--semi-color-primary-light-default)", borderRadius: 6, border: "1px solid var(--semi-color-primary-light-active)" }}>
            <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
              切换到：{selectedUser.name}
              <span style={{ fontSize: 11, color: "var(--semi-color-text-2)", marginLeft: 6 }}>
                ({selectedUser.teamName} · {selectedUser.roleName})
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Input placeholder="输入密码" value={password} onChange={setPassword} style={{ flex: 1 }} showClear type="password"
                onKeyDown={(e) => { if (e.key === "Enter") handleSwitch(); }} />
              <Button type="primary" loading={switching} onClick={handleSwitch}>确认切换</Button>
            </div>
          </div>
        )}

        <div style={{ fontSize: 11, color: "var(--semi-color-text-2)", textAlign: "center" }}>点击用户选中，输入密码后确认切换</div>
      </div>
    </Modal>
  );
}

const thStyle: React.CSSProperties = {
  padding: "8px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--semi-color-text-2)", whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "8px 10px", whiteSpace: "nowrap",
};
